// Shared plan pricing & feature catalogue — the single source of truth for the
// signup plan picker and the /app/plans page. Amounts are in paise (Razorpay
// convention) so they line up with the billing layer. Pricing rules:
//   Half-yearly = 10% off six months of the monthly price.
//   Yearly      = ten months of the monthly price (two months free).
// The half-yearly / yearly figures are stored explicitly (not recomputed) so the
// rounded rupee amounts shown to users are exact and reviewable in one place.
//
// i18n CONTRACT FOR THIS MODULE (Conv-30). Every user-facing string below —
// cadence labels + savings badges, tier names, taglines and the feature bullets —
// is the CANONICAL ENGLISH, and the English source string IS the translation key.
// This is a plain .ts module with no React context, so it cannot call useT(); the
// CONSUMER must render each one through t():
//
//     {t(tier.name)} · {t(tier.tagline)} · {tier.features.map((f) => <li>{t(f)}</li>)}
//     CADENCES.map((c) => ({ label: t(c.label), badge: c.savings ? t(c.savings) : undefined }))
//
// Consequences: keep these as inline literals (a computed/concatenated string has
// no key a catalog can carry), and treat any reword as a new key that needs its
// six translations. Consumers today: src/pages/auth/PlanPicker.tsx, src/pages/app/Plans.tsx.

import type { TierId } from './types';

export type Cadence = 'monthly' | 'halfYearly' | 'yearly';

export interface CadenceMeta {
  value: Cadence;
  /** English source string — render as t(label). */
  label: string;
  months: number;
  /** Short savings callout, null for the baseline monthly cadence. Render as t(savings). */
  savings: string | null;
}

// Render `label` and `savings` through t() at the call site — see the i18n note at
// the top of this file. `value` is a logic key and must NOT be translated.
export const CADENCES: CadenceMeta[] = [
  { value: 'monthly', label: 'Monthly', months: 1, savings: null },
  { value: 'halfYearly', label: 'Half-yearly', months: 6, savings: 'Save 10%' },
  { value: 'yearly', label: 'Yearly', months: 12, savings: '2 months free' },
];

export interface TierPricing {
  monthly: number;
  halfYearly: number;
  yearly: number;
}

export interface PaidTier {
  id: TierId;
  /** Display name — English source string, render as t(name). */
  name: string;
  /** English source string — render as t(tagline). */
  tagline: string;
  /** Amounts in paise per cadence. */
  pricing: TierPricing;
  /** English source strings — render as t(feature). */
  features: string[];
  /** Unused since 30 Oct 2026 (founder removed the "Most popular" badge — an
   *  invite-only app has no population to be popular with). Kept for the wire type. */
  highlight?: boolean;
}

// `name`, `tagline` and every `features` bullet are English source strings; the
// consumer renders them through t() (see the i18n note at the top of this file).
// `id` is a logic key and must NOT be translated.
//
// CONV-56 (founder decision 2026-09-06) — the ladder is Free → Gold → Platinum.
// Every account starts on the FREE card below and stays there until they buy
// something; the trial and the ₹299 Silver card are gone.
//
// These three feature lists are one of THREE IDENTICAL COPIES (server/razorpay.mjs,
// mobile/src/core/plans.ts) that publish onto the public GET /api/billing/plans, and
// `conv35-date-honesty.test.mjs` PARSES all three and asserts them equal. Edit all
// three or the build goes red — which is the point.
export const PAID_TIERS: PaidTier[] = [
  {
    // Conv-56 (founder decision 2026-09-06): the ₹299 Silver card is GONE and
    // the one-month trial with it. The ladder is Free → Gold → Platinum; this
    // card is the floor every account lives on, priced at exactly what it costs
    // (zero). The bullets scope what Free IS — the user's own portfolio —
    // because a free card that over-promises is the false-promise class and one
    // that under-sells deletes the user who would have stayed. The CONV-48
    // step-discount ruling below carries over verbatim: the step bullet is
    // removed, not reworded, and must not return until F152's blockers clear.
    id: 'FREE',
    name: 'Free',
    tagline: 'Your whole portfolio, one calm place — free, for as long as you like',
    pricing: { monthly: 0, halfYearly: 0, yearly: 0 },
    features: [
      'Your portfolio — stocks, MFs, gold, FDs & real estate — tracked free, forever',
      'CAS PDF import — bring your whole portfolio in from day one',
      'Portfolio analytics: allocation, concentration, XIRR & expense ratios',
      'Unlimited watchlists and price alerts',
      'Daily market digest, factual screeners and the full Learn library',
      'Savings goals, milestones, nominee hub and risk profile',
      'Share a view of your portfolio with a partner — free',
      'Net worth that subtracts the debts you record',
      'Your data is yours: full export and delete, always free',
    ],
  },
  {
    id: 'GOLD_ELITE',
    name: 'Gold Elite',
    tagline: 'Deeper tools for serious trackers',
    pricing: { monthly: 75900, halfYearly: 409800, yearly: 759000 },
    features: [
      'Everything in Free',
      'Benchmarking against market indices',
      'Scenario projections, financial-independence planning and peer benchmarking',
      'Household aggregation with consented family totals',
      'Masked share cards and commodity portfolio tracking',
      'Custom notification sounds & priority support',
      'Strategy lenses (educational frameworks, no stock names)',
      'Sansara AI — factual answers about your own portfolio, never advice',
    ],
  },
  {
    id: 'PLATINUM_ELITE',
    name: 'Platinum Elite',
    tagline: 'The full Sansara cockpit',
    pricing: { monthly: 149900, halfYearly: 809400, yearly: 1499000 },
    features: [
      'Everything in Gold Elite',
      'Estate vault, entities, access controls & proof of wealth',
      'Private investments folded into your net worth',
      'Read-only API access',
      'Early access to every switch-on feature as it goes live',
      'Concierge onboarding call',
    ],
  },
];

export type PlanSelectionId = TierId;

/**
 * The tier every new account starts on — permanently, not as a trial (Conv-56).
 * Mirrors the server's ENTRY_TIER — named rather than inlined so the signup
 * default and any copy about the floor can never drift into two different
 * opinions about where the ladder starts.
 */
export const ENTRY_TIER: TierId = 'FREE';

export const tierById = (id: TierId): PaidTier | undefined => PAID_TIERS.find((t) => t.id === id);

// Ordinal rank of each tier (FREE=0 … PLATINUM_ELITE=2), derived from PAID_TIERS
// order so it can never drift from the catalogue. Used by the plans UI to label
// buttons by direction — a higher tier is an "Upgrade", a lower tier is already
// "Included" — instead of calling every non-current tier an upgrade.
export const TIER_RANK: Record<TierId, number> = PAID_TIERS.reduce(
  (acc, t, i) => {
    acc[t.id] = i;
    return acc;
  },
  {} as Record<TierId, number>,
);

/** Rank of a tier, or -1 for an unknown/absent id (fails safe — never a false "current"). */
export const tierRank = (id: TierId | null | undefined): number =>
  id != null && id in TIER_RANK ? TIER_RANK[id] : -1;

/** Feature bullets for a tier — English source strings; render each through t(). */
export const featuresFor = (id: TierId): string[] => tierById(id)?.features ?? [];

export const priceFor = (tier: PaidTier, cadence: Cadence): number => tier.pricing[cadence];

const monthsFor = (cadence: Cadence): number => CADENCES.find((c) => c.value === cadence)?.months ?? 1;

/** Effective per-month price in paise for the given cadence (rounded to paise). */
export const perMonthPaise = (tier: PaidTier, cadence: Cadence): number =>
  Math.round(priceFor(tier, cadence) / monthsFor(cadence));

/** Savings badge for a cadence — an English source string; render through t(). */
export const savingsFor = (cadence: Cadence): string | null =>
  CADENCES.find((c) => c.value === cadence)?.savings ?? null;

/**
 * Every paid price is quoted EXCLUSIVE of GST (founder ruling 2026-10-04), so a
 * price never renders without saying so. "GST" is the statutory name of the tax
 * and stays in Latin script in every locale, exactly as SEBI and ₹ do — which is
 * why this is a suffix and not a catalog key. The RATE is never typed on a
 * client: it comes off the order or off GET /api/billing/plans (`gst.ratePct`).
 */
export const withGst = (price: string): string => `${price} + GST`;

/** Format paise to a rupee string with en-IN grouping and no paise digits. */
export const formatInr = (paise: number): string => `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;
